// Gemeinsame Bausteine der Leergut-Verträge. Stückzahlen stehen in Tausendstel (1 Kasten = 1000, 20 Flaschen =
// 20000), Geld in Cent. Ein Gebindepfand (Kasten 20 × 0,5 l) hat keinen eigenen Betrag: Er ist die Summe seiner
// Bestandteile (Leerkasten + 20 Flaschen). Konten und Leergutbestand rechnen in diesen einfachen Pfandarten.
import { zId } from '@shared/ids'
import { z } from 'zod'

/** Kalendertag 'YYYY-MM-DD' (Europe/Berlin). */
export const zDay = z.iso.date({ error: 'Bitte ein gültiges Datum eingeben.' })

export const zText = (max: number) => z.string({ error: 'Bitte ausfüllen.' }).trim().min(1, { error: 'Bitte ausfüllen.' }).max(max)

export const zOptionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) => (value === '' ? null : value))

/** Stück in Tausendstel, größer als null und ganze Stücke. */
export const zPieceQty = z
  .int({ error: 'Bitte eine Stückzahl eingeben.' })
  .min(1000, { error: 'Bitte mindestens 1 Stück angeben.' })
  .max(100_000_000, { error: 'Die Stückzahl ist zu groß.' })
  .multipleOf(1000, { error: 'Leergut wird in ganzen Stücken gezählt.' })

/** Stück in Tausendstel, null erlaubt (Inhalt eines Mischkastens). */
export const zPieceQtyOrZero = z
  .int({ error: 'Bitte eine Stückzahl eingeben.' })
  .min(0, { error: 'Die Stückzahl darf nicht negativ sein.' })
  .max(100_000_000, { error: 'Die Stückzahl ist zu groß.' })
  .multipleOf(1000, { error: 'Leergut wird in ganzen Stücken gezählt.' })

export const zDepositCents = z.int({ error: 'Bitte einen Betrag eingeben.' }).min(0, { error: 'Der Pfandbetrag darf nicht negativ sein.' }).max(10_000_000, { error: 'Der Pfandbetrag ist zu hoch.' })

export const zReason = z
  .string({ error: 'Bitte geben Sie einen Grund an.' })
  .trim()
  .min(3, { error: 'Bitte geben Sie einen Grund an (mindestens 3 Zeichen).' })
  .max(500)

export const zSaved = z.object({ id: zId, version: z.int() })
export const zById = z.strictObject({ id: zId })
export const zExpectedVersion = z.int().positive().optional()
export const zLocationRef = z.object({ id: zId, code: z.string(), name: z.string() })

export const DEPOSIT_KINDS = ['reusable', 'single_use_dpg', 'transport_aid', 'rental_container'] as const
export type DepositKind = (typeof DEPOSIT_KINDS)[number]

export const DEPOSIT_ACCOUNTS = ['customer', 'supplier'] as const
export type DepositAccountSide = (typeof DEPOSIT_ACCOUNTS)[number]

/** Gezähltes Leergut: Pfandart (einfach oder Gebinde), Stückzahl und – bei Mischkästen – der tatsächliche Inhalt. */
export const zReturnEntry = z.strictObject({
  depositTypeId: zId,
  qty: zPieceQty.describe('Stück der Pfandart in Tausendstel (3 Kästen = 3000)'),
  /**
   * Nur bei Gebinden mit abweichendem Inhalt (Mischkasten, Kasten mit 17 Flaschen): tatsächlicher Inhalt ALLER
   * Gebinde der Zeile in einfachen Pfandarten. Ohne Angabe gilt der volle Inhalt.
   */
  contents: z.array(z.strictObject({ depositTypeId: zId, qty: zPieceQtyOrZero })).max(20).optional(),
})

export const zReturnEntries = z.array(zReturnEntry).min(1, { error: 'Bitte zählen Sie mindestens eine Pfandart.' }).max(100)
